import {
  FileText,
  Users,
  Layers,
  Image as ImageIcon,
  Film,
  Sparkles,
  Wrench,
  Check,
  Type,
  CreditCard,
  Zap,
  Play,
  Search,
  Grid3X3,
  FolderTree,
  CircleHelp,
  ClipboardList,
  Clapperboard,
  Upload as UploadIcon,
  Music,
} from 'lucide-react'
import type { ReactNode } from 'react'

export interface ToolDisplayContext {
  /** Look up scene number (1-indexed) by scene ID */
  getSceneNumber?: (sceneId: string) => number | undefined
}

export interface ToolDisplayConfig {
  /** Display name for the tool (static, e.g., "Generate Video") */
  name: string
  /** Active/running form (e.g., "Generating video...") */
  activeName?: string
  /** Icon component (size 12) */
  icon: ReactNode
  /**
   * Format the inline display with params.
   * Returns a string or ReactNode for custom rendering.
   * If not provided, just shows the tool name.
   */
  formatInline?: (input: Record<string, unknown> | null, ctx?: ToolDisplayContext) => string | null
}

/**
 * Configuration for how each tool is displayed inline in the chat.
 * Each tool can specify:
 * - name: The human-readable name
 * - icon: A Lucide icon (size 12)
 * - formatInline: A function that formats the input params for display
 */
export const TOOL_DISPLAY_CONFIG: Record<string, ToolDisplayConfig> = {
  set_title: {
    name: 'Set Title',
    activeName: 'Setting title',
    icon: <Type size={12} />,
    formatInline: (input) => {
      const title = input?.title as string | undefined
      return title ? `"${truncate(title, 30)}"` : null
    },
  },
  update_narrative: {
    name: 'Update Story',
    activeName: 'Updating story',
    icon: <FileText size={12} />,
    formatInline: (input) => {
      const content = input?.content as string | undefined
      if (!content) return null
      const words = content.split(/\s+/).slice(0, 6).join(' ')
      return truncate(words, 40)
    },
  },
  update_entities: {
    name: 'Update Entities',
    activeName: 'Updating entities',
    icon: <Users size={12} />,
    formatInline: (input) => {
      const kind = input?.entity_type as string | undefined
      const entities = input?.entities
      const label = kind ? `${kind}${Array.isArray(entities) && entities.length !== 1 ? 's' : ''}` : 'entities'
      if (!Array.isArray(entities) || entities.length === 0) return kind ?? null
      const names = entities.slice(0, 2).map((e: { name?: string }) => e.name).filter(Boolean)
      if (names.length === 0) return `${entities.length} ${label}`
      return names.join(', ') + (entities.length > 2 ? ` +${entities.length - 2}` : '')
    },
  },
  update_brief: {
    name: 'Update Brief',
    activeName: 'Updating brief',
    icon: <ClipboardList size={12} />,
    formatInline: (input) => {
      if (!input) return null
      const fields = Object.keys(input).filter(k => input[k] !== undefined)
      if (fields.length === 0) return null
      return fields.slice(0, 3).join(', ') + (fields.length > 3 ? ` +${fields.length - 3}` : '')
    },
  },
  request_assets: {
    name: 'Request Files',
    activeName: 'Requesting files',
    icon: <UploadIcon size={12} />,
    formatInline: (input) => {
      const requests = input?.requests as Array<{ label?: string }> | undefined
      if (!requests?.length) return null
      const labels = requests.slice(0, 2).map(r => r.label).filter(Boolean)
      return labels.join(', ') + (requests.length > 2 ? ` +${requests.length - 2}` : '')
    },
  },
  show_treatment: {
    name: 'Treatment',
    activeName: 'Preparing treatment',
    icon: <Clapperboard size={12} />,
    formatInline: (input) => {
      const logline = input?.logline as string | undefined
      return logline ? truncate(logline, 40) : null
    },
  },
  generate_references: {
    name: 'Generate Refs',
    activeName: 'Generating reference options (~1–2 min)',
    icon: <ImageIcon size={12} />,
    formatInline: (input) => {
      const kind = input?.entity_type as string | undefined
      const parts: string[] = []
      if (kind) parts.push(kind)
      if (input?.selected_asset_id) {
        parts.push('locking pick')
      } else {
        const poses = input?.poses as string[] | undefined
        const briefs = input?.option_briefs as string[] | undefined
        const num = (input?.num_options as number | undefined) ?? briefs?.length
        if (kind === 'character' && poses && poses.length > 0) parts.push(`${poses.length} poses`)
        else if (num) parts.push(`${num} takes`)
      }
      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  upsert_scene: {
    name: 'Scene',
    activeName: 'Updating scene',
    icon: <Layers size={12} />,
    formatInline: (input, ctx) => {
      const sceneId = input?.scene_id as string | undefined
      const title = input?.title as string | undefined
      const hasPrompt = !!input?.video_prompt
      const parts: string[] = []

      // Show scene number if updating
      if (sceneId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(sceneId)
        if (sceneNum) parts.push(`Scene ${sceneNum}`)
      }

      if (title) {
        parts.push(`"${truncate(title, 20)}"`)
      }

      if (hasPrompt) {
        parts.push('+ prompt')
      }

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  delete_scene: {
    name: 'Delete Scene',
    icon: <Layers size={12} />,
    formatInline: (input, ctx) => {
      const sceneId = input?.scene_id as string | undefined
      if (sceneId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(sceneId)
        if (sceneNum) return `Scene ${sceneNum}`
      }
      return null
    },
  },
  set_workflow_phase: {
    name: 'Set Phase',
    icon: <Wrench size={12} />,
    formatInline: (input) => {
      const phase = input?.phase as string | undefined
      if (!phase) return null
      // Capitalize first letter
      return phase.charAt(0).toUpperCase() + phase.slice(1)
    },
  },
  reorder_scenes: {
    name: 'Reorder Scenes',
    icon: <Layers size={12} />,
    formatInline: (input) => {
      const order = input?.scene_order as string[] | undefined
      return order ? `${order.length} scenes` : null
    },
  },
  edit_timeline: {
    name: 'Edit Timeline',
    activeName: 'Editing timeline',
    icon: <Layers size={12} />,
    formatInline: (input) => {
      const ops = input?.operations as Array<{ op?: string }> | undefined
      if (!Array.isArray(ops) || ops.length === 0) return null
      const labels: Record<string, string> = {
        reorder: 'reorder',
        trim: 'trim',
        set_speed: 'speed',
        set_transition: 'transition',
      }
      const names = ops.slice(0, 3).map(o => labels[o.op ?? ''] ?? o.op).filter(Boolean)
      return names.join(', ') + (ops.length > 3 ? ` +${ops.length - 3}` : '')
    },
  },
  audio_track: {
    name: 'Audio Track',
    activeName: 'Editing audio',
    icon: <Music size={12} />,
    formatInline: (input) => {
      const op = input?.op as string | undefined
      const verbs: Record<string, string> = {
        add: 'Add',
        update: 'Update',
        remove: 'Remove',
        list: 'List',
      }
      const parts: string[] = []
      if (op) parts.push(verbs[op] ?? op)
      const type = (input?.type || input?.name) as string | undefined
      if (type) parts.push(type)
      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  text_overlay: {
    name: 'Text Overlay',
    activeName: 'Editing text',
    icon: <Type size={12} />,
    formatInline: (input) => {
      const op = input?.op as string | undefined
      const text = input?.text as string | undefined
      const parts: string[] = []
      const verbs: Record<string, string> = { add: 'Add', update: 'Update', remove: 'Remove' }
      if (op) parts.push(verbs[op] ?? op)
      if (text) parts.push(`"${truncate(text, 24)}"`)
      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  generate_keyframes: {
    name: 'Generate Keyframes',
    activeName: 'Generating keyframes',
    icon: <Sparkles size={12} />,
    formatInline: (input, ctx) => {
      const clipId = input?.clip_id as string | undefined
      const count = input?.keyframe_count as number | undefined
      const parts: string[] = []

      if (clipId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(clipId)
        if (sceneNum) parts.push(`Clip ${sceneNum}`)
      }

      if (count) parts.push(`${count} frames`)

      return parts.length > 0 ? parts.join(' · ') : 'start + end'
    },
  },
  regenerate_keyframe: {
    name: 'Regenerate Keyframe',
    activeName: 'Regenerating keyframe',
    icon: <Sparkles size={12} />,
    formatInline: (input, ctx) => {
      const clipId = input?.clip_id as string | undefined
      const timestamp = input?.timestamp as number | undefined
      const parts: string[] = []

      if (clipId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(clipId)
        if (sceneNum) parts.push(`Clip ${sceneNum}`)
      }

      if (timestamp !== undefined) {
        const label = timestamp === 0 ? 'start' : timestamp === 1 ? 'end' : 'mid'
        parts.push(label)
      }

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  generate_scene_video: {
    name: 'Generate Video',
    activeName: 'Generating video',
    icon: <Film size={12} />,
    formatInline: (input, ctx) => {
      const imageUrl = input?.image_url as string | undefined
      const useT2V = input?.use_text_to_video as boolean | undefined
      const sceneId = (input?.scene_id || input?.clip_id) as string | undefined

      const parts: string[] = []

      // Add scene/clip number if available
      if (sceneId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(sceneId)
        if (sceneNum) parts.push(`Clip ${sceneNum}`)
      }

      // Determine generation mode
      if (useT2V) {
        parts.push('text-to-video')
      } else if (imageUrl) {
        parts.push('with ref image')
      } else {
        // No explicit image_url and not text-to-video means it will use previous scene's frame
        parts.push('from prev frame')
      }

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  generate_clip_video: {
    name: 'Generate Video',
    activeName: 'Generating video',
    icon: <Film size={12} />,
    formatInline: (input, ctx) => {
      const imageUrl = input?.image_url as string | undefined
      const useT2V = input?.use_text_to_video as boolean | undefined
      const clipId = input?.clip_id as string | undefined

      const parts: string[] = []

      // Add clip number if available
      if (clipId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(clipId)
        if (sceneNum) parts.push(`Clip ${sceneNum}`)
      }

      // Determine generation mode
      const mode = input?.mode as string | undefined
      if (mode) {
        parts.push(mode)
      } else if (useT2V) {
        parts.push('text-to-video')
      } else if (imageUrl) {
        parts.push('with ref image')
      }

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  get_video_frame: {
    name: 'Extract Frame',
    activeName: 'Extracting frame',
    icon: <Sparkles size={12} />,
    formatInline: (input, ctx) => {
      const sceneId = input?.scene_id as string | undefined
      const timestamp = input?.timestamp as number | undefined
      const parts: string[] = []

      if (sceneId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(sceneId)
        if (sceneNum) parts.push(`Scene ${sceneNum}`)
      }

      if (timestamp !== undefined) {
        parts.push(timestamp === -1 ? 'last frame' : `${timestamp}s`)
      }

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  inspect_frame: {
    name: 'Inspect Frame',
    activeName: 'Inspecting frame',
    icon: <Sparkles size={12} />,
    formatInline: (input) => {
      const questions = input?.questions as string[] | undefined
      if (Array.isArray(questions) && questions.length > 0) {
        return questions.length === 1 ? truncate(questions[0], 40) : `${questions.length} questions`
      }
      return input?.asset_id ? 'frame' : null
    },
  },
  analyze_video_frames: {
    name: 'Analyze Video',
    activeName: 'Analyzing video',
    icon: <Sparkles size={12} />,
    formatInline: (input, ctx) => {
      const sceneId = input?.scene_id as string | undefined
      const numFrames = input?.num_frames as number | undefined
      const parts: string[] = []

      if (sceneId && ctx?.getSceneNumber) {
        const sceneNum = ctx.getSceneNumber(sceneId)
        if (sceneNum) parts.push(`Scene ${sceneNum}`)
      }

      if (numFrames) parts.push(`${numFrames} frames`)

      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  analyze_media: {
    name: 'Analyze Media',
    activeName: 'Analyzing media',
    icon: <ImageIcon size={12} />,
    formatInline: (input) => {
      const files = input?.files as unknown[] | undefined
      const focus = input?.focus as string | undefined
      const parts: string[] = []
      if (Array.isArray(files) && files.length > 0) {
        parts.push(`${files.length} file${files.length === 1 ? '' : 's'}`)
      }
      if (focus) parts.push(truncate(focus, 28))
      return parts.length > 0 ? parts.join(' · ') : null
    },
  },
  export: {
    name: 'Export',
    icon: <Check size={12} />,
    formatInline: () => null,
  },
  acknowledge_notification: {
    name: 'Acknowledge',
    icon: <Check size={12} />,
    formatInline: () => null, // Internal, no need to show reason
  },
  show_upgrade_card: {
    name: 'Upgrade',
    icon: <Zap size={12} />,
    formatInline: (input) => {
      const needed = input?.credits_needed as number | undefined
      return needed ? `${needed.toLocaleString()} credits needed` : null
    },
  },
  end_trial_early: {
    name: 'Activate Subscription',
    activeName: 'Activating subscription',
    icon: <CreditCard size={12} />,
    formatInline: () => null,
  },
  show_manage_subscription: {
    name: 'Manage Subscription',
    icon: <CreditCard size={12} />,
    formatInline: () => null,
  },
  upgrade_to_max: {
    name: 'Upgrade to Max',
    activeName: 'Upgrading to Max',
    icon: <Zap size={12} />,
    formatInline: () => null,
  },
  upgrade_to_ultra: {
    name: 'Upgrade to Ultra',
    activeName: 'Upgrading to Ultra',
    icon: <Zap size={12} />,
    formatInline: () => null,
  },
  confirm_scenes_and_generate: {
    name: 'Confirm & Generate',
    icon: <Play size={12} />,
    formatInline: (input) => {
      const model = input?.model as string | undefined
      return model ? model.toUpperCase() : null
    },
  },
  get_credits: {
    name: 'Get Credits',
    icon: <CreditCard size={12} />,
    formatInline: () => null,
  },
  generate_image: {
    name: 'Generate Image',
    activeName: 'Generating image',
    icon: <ImageIcon size={12} />,
    formatInline: (input) => {
      const aspectRatio = input?.aspect_ratio as string | undefined
      const purpose = input?.purpose as string | undefined
      const parts: string[] = []

      if (purpose) parts.push(purpose)
      if (aspectRatio) parts.push(aspectRatio)

      return parts.length > 0 ? parts.join(' · ') : 'Nano Banana Pro'
    },
  },
  web_search: {
    name: 'Web Search',
    activeName: 'Searching',
    icon: <Search size={12} />,
    formatInline: () => null,
  },
  assets: {
    name: 'Assets',
    activeName: 'Browsing assets',
    icon: <Search size={12} />,
    formatInline: (input) => {
      const query = input?.query as string | undefined
      if (query) return truncate(query, 40)
      if (input?.asset_id) return 'asset details'
      return null
    },
  },
  transcribe: {
    name: 'Transcribe',
    activeName: 'Transcribing audio',
    icon: <FileText size={12} />,
    formatInline: () => null,
  },
  ask_questions: {
    name: 'Questions',
    activeName: 'Asking',
    icon: <CircleHelp size={12} />,
    formatInline: (input) => {
      const questions = input?.questions as Array<{ question?: string }> | undefined
      if (!questions?.length) return null
      return `${questions.length} questions`
    },
  },
  show_media_grid: {
    name: 'Media Grid',
    activeName: 'Loading media',
    icon: <Grid3X3 size={12} />,
    formatInline: (input) => {
      const items = input?.items as unknown[] | undefined
      return items ? `${items.length} items` : null
    },
  },
  show_clusters: {
    name: 'Media Clusters',
    activeName: 'Loading clusters',
    icon: <Layers size={12} />,
    formatInline: (input) => {
      const clusters = input?.clusters as unknown[] | undefined
      return clusters ? `${clusters.length} groups` : null
    },
  },
  show_folder_preview: {
    name: 'Folder Preview',
    activeName: 'Loading folder',
    icon: <FolderTree size={12} />,
    formatInline: (input) => {
      const rootPath = input?.root_path as string | undefined
      if (!rootPath) return null
      return rootPath.split('/').filter(Boolean).pop() || rootPath
    },
  },
  read_url_content: {
    name: 'Read URL Content',
    activeName: 'Reading URL',
    icon: <Search size={12} />,
    formatInline: (input) => {
      const url = input?.url as string | undefined
      const crawl = input?.crawl as boolean | undefined
      const parts: string[] = []

      if (url) parts.push(simplifyUrl(url))
      if (crawl === true) parts.push('crawl: on')
      if (crawl === false) parts.push('crawl: off')

      return parts.length > 0 ? parts.join(' · ') : formatToolInputFallbackDetail(input, undefined, false)
    },
  },
}

const FALLBACK_PARAM_KEYS = [
  'url',
  'query',
  'search_query',
  'title',
  'prompt',
  'text',
  'content',
  'model',
  'aspect_ratio',
  'duration',
  'timestamp',
] as const

const FALLBACK_PARAM_LABELS: Record<string, string> = {
  url: 'URL',
  query: 'Query',
  search_query: 'Query',
  title: 'Title',
  prompt: 'Prompt',
  text: 'Text',
  content: 'Content',
  model: 'Model',
  aspect_ratio: 'Ratio',
  duration: 'Duration',
  timestamp: 'Time',
}

export function formatToolInputFallbackDetail(
  input: Record<string, unknown> | null,
  ctx?: ToolDisplayContext,
  includeSceneRefs = true
): string | null {
  if (!input) return null

  const parts: string[] = []
  const consumedKeys = new Set<string>()

  const pushPart = (part: string | null) => {
    if (!part) return
    if (parts.includes(part)) return
    parts.push(part)
  }

  const addSceneRef = (key: 'scene_id' | 'clip_id', label: 'Scene' | 'Clip') => {
    const value = input[key]
    if (typeof value !== 'string' || !value) return
    consumedKeys.add(key)
    if (ctx?.getSceneNumber) {
      const num = ctx.getSceneNumber(value)
      if (num) {
        pushPart(`${label} ${num}`)
        return
      }
    }
    pushPart(`${label} ${value.slice(-4)}`)
  }

  const addKey = (key: string, includeLabel = true) => {
    if (!Object.prototype.hasOwnProperty.call(input, key)) return
    if (consumedKeys.has(key)) return
    const value = input[key]
    const rendered = formatToolParamValue(key, value)
    if (!rendered) return
    consumedKeys.add(key)
    const label = includeLabel ? (FALLBACK_PARAM_LABELS[key] || humanizeParamKey(key)) : undefined
    pushPart(label ? `${label}: ${rendered}` : rendered)
  }

  if (includeSceneRefs) {
    addSceneRef('scene_id', 'Scene')
    addSceneRef('clip_id', 'Clip')
  }

  for (const key of FALLBACK_PARAM_KEYS) {
    addKey(key)
    if (parts.length >= 2) {
      return parts.join(' · ')
    }
  }

  for (const [key] of Object.entries(input)) {
    if (parts.length >= 2) break
    if (key.endsWith('_id')) continue
    addKey(key, true)
  }

  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Get the display configuration for a tool.
 * Falls back to a default config if the tool isn't in the config.
 */
export function getToolDisplayConfig(toolName: string): ToolDisplayConfig {
  return TOOL_DISPLAY_CONFIG[toolName] || {
    name: formatToolNameDefault(toolName),
    icon: <Wrench size={12} />,
  }
}

/**
 * Format a tool name and its input for inline display.
 * Returns { name, activeName, detail, icon }
 */
export function formatToolInline(
  toolName: string,
  input: Record<string, unknown> | null,
  ctx?: ToolDisplayContext
): { name: string; activeName: string; detail: string | null; icon: ReactNode } {
  const config = getToolDisplayConfig(toolName)
  const detail = config.formatInline?.(input, ctx) ?? null
  return {
    name: config.name,
    activeName: config.activeName || config.name,
    detail,
    icon: config.icon,
  }
}

/** Default tool name formatter (snake_case to Title Case) */
function formatToolNameDefault(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/** Truncate a string with ellipsis */
function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 1) + '…'
}

function formatToolParamValue(key: string, value: unknown): string | null {
  if (key === 'url' && typeof value === 'string') {
    return simplifyUrl(value)
  }

  if (typeof value === 'string') {
    const compact = value.replace(/\s+/g, ' ').trim()
    if (!compact) return null
    return `"${truncate(compact, 32)}"`
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null
    if (value.every((item) => typeof item === 'string' || typeof item === 'number')) {
      const preview = value
        .slice(0, 2)
        .map((item) => typeof item === 'string' ? truncate(item, 14) : String(item))
        .join(', ')
      return value.length > 2 ? `${preview} +${value.length - 2}` : preview
    }
    return `${value.length} items`
  }

  return null
}

function simplifyUrl(value: string): string {
  try {
    const parsed = new URL(value)
    const path = parsed.pathname === '/' ? '' : parsed.pathname
    return `${parsed.hostname}${truncate(path, 20)}`
  } catch {
    return truncate(value, 32)
  }
}

function humanizeParamKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
